/**
 * IDD (Input Data Dictionary) Schema Types
 *
 * These types represent the parsed structure of an EnergyPlus IDD file,
 * which defines all valid object types, their fields, and validation rules.
 */

/**
 * The complete IDD schema containing all object definitions
 */
export interface IDDSchema {
  /** EnergyPlus version this IDD corresponds to */
  version: string;
  /** Build identifier from the IDD */
  build?: string;
  /** Object types organized by group */
  groups: IDDGroup[];
  /** Fast lookup map for object types by name (case-insensitive) */
  objectTypes: Map<string, IDDObjectType>;
  /** Reference lists mapping list names to object class names that belong to them */
  referenceLists: Map<string, Set<string>>;
  /** Object-list mappings: which object types accept references from which lists */
  objectListMappings: Map<string, string[]>;
}

/**
 * A group of related object types in the IDD
 */
export interface IDDGroup {
  /** Group name (e.g., "Simulation Parameters", "Location and Climate") */
  name: string;
  /** Object type names in this group */
  objectTypes: string[];
}

/**
 * Definition of an EnergyPlus object type
 */
export interface IDDObjectType {
  /** Object class name (e.g., "Building", "Zone", "Material") */
  name: string;
  /** Group this object belongs to */
  group: string;
  /** Documentation from \memo tags */
  memo: string;
  /** Field definitions */
  fields: IDDField[];
  /** Minimum number of fields required */
  minFields: number;
  /** Only one instance of this object allowed in a model */
  isUnique: boolean;
  /** This object must exist in every model */
  isRequired: boolean;
  /** Object is obsolete and should not be used */
  isObsolete: boolean;
  /** Format specification (e.g., "singleLine", "vertices") */
  format?: string;
  /** Number of fields in extensible group (0 if not extensible) */
  extensible: number;
  /** Starting field index for extensible portion */
  extensibleBegin?: number;
}

/**
 * Field type enumeration
 */
export type IDDFieldType =
  | 'real' // Floating point number
  | 'integer' // Integer number
  | 'alpha' // Text string
  | 'choice' // One of predefined choices
  | 'object-list' // Reference to another object
  | 'external-list' // Reference to external data
  | 'node'; // HVAC node name

/**
 * Definition of a single field within an object type
 */
export interface IDDField {
  /** Field identifier (A1, A2, N1, N2, etc.) */
  id: string;
  /** Field name from \field tag */
  name: string;
  /** Data type */
  type: IDDFieldType;
  /** Whether this field is required */
  required: boolean;
  /** Default value */
  default?: string;
  /** Unit specification (e.g., "m", "W", "degC") */
  units?: string;
  /** Unit conversion basis for IP units */
  unitsBasedOnField?: string;
  /** Minimum value (inclusive unless exclusiveMinimum) */
  minimum?: number;
  /** Whether minimum is exclusive (> instead of >=) */
  exclusiveMinimum?: boolean;
  /** Maximum value (inclusive unless exclusiveMaximum) */
  maximum?: number;
  /** Whether maximum is exclusive (< instead of <=) */
  exclusiveMaximum?: boolean;
  /** Valid choices for 'choice' type fields */
  choices?: string[];
  /** Object-list name for reference fields */
  objectList?: string;
  /** External-list name for fields referencing external data (e.g., autoRDDvariable, autoRDDmeter) */
  externalList?: string;
  /** Reference list names this field's value can be referenced by */
  references?: string[];
  /** Documentation text from \memo and \note tags */
  memo: string;
  /** Whether this field can be autosized */
  autosizable: boolean;
  /** Whether this field can be autocalculated */
  autocalculatable: boolean;
  /** IP unit specification */
  ipUnits?: string;
  /** Field is deprecated */
  deprecated?: boolean;
  /** Whether field must have begin-extensible marker */
  beginExtensible?: boolean;
  /** Retains value from previous timestep */
  retaincase?: boolean;
}

/**
 * Token types for the IDD lexer
 */
export type IDDTokenType =
  | 'GroupHeader' // \group Name
  | 'ClassName' // Object class name at start of definition
  | 'FieldId' // A1, N1, etc.
  | 'Comma' // ,
  | 'Semicolon' // ;
  | 'Backslash' // \ (start of property)
  | 'PropertyName' // memo, field, type, etc.
  | 'PropertyValue' // Value after property name
  | 'Comment' // ! comment
  | 'Newline'
  | 'Whitespace'
  | 'EOF';

/**
 * A token from the IDD lexer
 */
export interface IDDToken {
  type: IDDTokenType;
  value: string;
  line: number;
  column: number;
}

/**
 * IDD parsing error
 */
export interface IDDParseError {
  message: string;
  line: number;
  column: number;
}

/**
 * Result of parsing an IDD file
 */
export interface IDDParseResult {
  schema: IDDSchema | null;
  errors: IDDParseError[];
}

/**
 * Known IDD property names
 */
export const IDD_PROPERTIES = [
  'memo',
  'note',
  'unique-object',
  'required-object',
  'min-fields',
  'obsolete',
  'extensible',
  'format',
  'field',
  'required-field',
  'begin-extensible',
  'type',
  'key',
  'object-list',
  'external-list',
  'reference',
  'reference-class-name',
  'units',
  'ip-units',
  'unitsBasedOnField',
  'minimum',
  'minimum>',
  'maximum',
  'maximum<',
  'default',
  'deprecated',
  'autosizable',
  'autocalculatable',
  'retaincase',
] as const;

export type IDDPropertyName = (typeof IDD_PROPERTIES)[number];

/**
 * Known field types in IDD
 */
export const IDD_FIELD_TYPES = [
  'real',
  'integer',
  'alpha',
  'choice',
  'object-list',
  'external-list',
  'node',
] as const;

/**
 * Helper to create an empty schema
 */
export function createEmptySchema(): IDDSchema {
  return {
    version: '',
    groups: [],
    objectTypes: new Map(),
    referenceLists: new Map(),
    objectListMappings: new Map(),
  };
}

/**
 * Helper to create a default field
 */
export function createDefaultField(id: string): IDDField {
  return {
    id,
    name: '',
    type: id.startsWith('A') ? 'alpha' : 'real',
    required: false,
    memo: '',
    autosizable: false,
    autocalculatable: false,
  };
}

/**
 * Helper to create a default object type
 */
export function createDefaultObjectType(name: string): IDDObjectType {
  return {
    name,
    group: '',
    memo: '',
    fields: [],
    minFields: 0,
    isUnique: false,
    isRequired: false,
    isObsolete: false,
    extensible: 0,
  };
}
